import * as React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { NotAssessed } from '@/lib/api/vendor-intelligence';
import { notMappedNote } from './vi-logic';

/**
 * The requirements not mapped to a library module, by client area (owner
 * decision, 24 October 2026): ratings are per library module, so no vendor
 * is rated on them and they are out of the module weights until mapped. The
 * report draft shows the frozen table after the comparison, with code's
 * sentence, as the PDF prints it; the Ratings step shows one collapsed
 * note (`NotMappedNote`) that opens to the same rows. Nothing when every
 * requirement is mapped.
 */
export function NotAssessedTable({
  data,
  note,
}: {
  data: NotAssessed | null | undefined;
  /** The sentence above the table; the tab's own when absent. */
  note?: string;
}) {
  if (!data || data.requirements === 0) return null;
  const sentence =
    note ||
    `${data.requirements.toLocaleString()} ${
      data.requirements === 1 ? 'requirement is' : 'requirements are'
    } not mapped to a library module yet, so no vendor is rated on ${
      data.requirements === 1 ? 'it' : 'them'
    } and the fit leaves ${data.requirements === 1 ? 'it' : 'them'} out. Map ${
      data.requirements === 1 ? 'it' : 'them'
    } to a module on the Requirements tab to bring ${
      data.requirements === 1 ? 'it' : 'them'
    } into the scores.`;
  return (
    <section aria-label="Not assessed yet" className="flex flex-col gap-2">
      <h3 className="m-0 text-h3 font-semibold text-ink-900">Not assessed yet</h3>
      <p className="m-0 text-body-sm text-ink-500">{sentence}</p>
      <NotAssessedRows data={data} />
    </section>
  );
}

/**
 * One line under the ratings grid (T-0272, E24-design §2): how many
 * requirements, in how many client areas, carry no module and so no
 * rating. It opens to the rows by area. Nothing when every requirement is
 * mapped.
 */
export function NotMappedNote({ data }: { data: NotAssessed | null | undefined }) {
  const [open, setOpen] = React.useState(false);
  const sentence = notMappedNote(data);
  if (!data || sentence === null) return null;
  return (
    <div className="flex-none rounded-card border border-ink-100">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 rounded-card px-3 py-2 text-left text-body-sm text-ink-700 hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-amber-600"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
        )}
        {sentence}
      </button>
      {open ? (
        <div className="flex flex-col gap-2 border-t border-ink-100 px-3 py-2.5">
          <p className="m-0 text-meta text-ink-500">
            Map {data.requirements === 1 ? 'it' : 'them'} to a module on the Requirements tab to
            bring {data.requirements === 1 ? 'it' : 'them'} into the scores.
          </p>
          <NotAssessedRows data={data} />
        </div>
      ) : null}
    </div>
  );
}

/** The rows by client area, with the total, as the PDF prints them. */
export function NotAssessedRows({ data }: { data: NotAssessed }) {
  return (
    <div className="overflow-x-auto rounded-card border border-ink-100">
      <table className="w-full border-collapse text-body-sm">
        <thead>
          <tr className="border-b border-ink-100 text-left text-meta font-medium text-ink-500">
            <th scope="col" className="px-3 py-2 font-medium">
              Client area
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Requirements
            </th>
            <th scope="col" className="px-3 py-2 text-right font-medium">
              Critical
            </th>
          </tr>
        </thead>
        <tbody>
          {data.areas.map((a) => (
            <tr key={a.area ?? ''} className="border-b border-ink-100">
              <th scope="row" className="px-3 py-2 text-left font-normal text-ink-900">
                {a.area ?? <span className="text-ink-500">No area</span>}
              </th>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-700">
                {a.requirements.toLocaleString()}
              </td>
              <td className="px-3 py-2 text-right font-mono tabular-nums text-ink-700">
                {a.critical.toLocaleString()}
              </td>
            </tr>
          ))}
          <tr>
            <th scope="row" className="px-3 py-2 text-left font-semibold text-ink-900">
              Total
            </th>
            <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-ink-900">
              {data.requirements.toLocaleString()}
            </td>
            <td className="px-3 py-2 text-right font-mono font-semibold tabular-nums text-ink-900">
              {data.critical.toLocaleString()}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
